import { useSettings } from '../context/SettingsContext';
import { Card } from '../components/ui/card';
import { SectionHeader } from '../components/SectionHeader';
import { toEmbedUrl } from '../lib/youtube';
import { ShieldCheck, GraduationCap, Heart, Sparkles, Anchor } from 'lucide-react';

export default function About() {
  const { settings } = useSettings();
  const aboutEmbed = settings?.about_video_url ? toEmbedUrl(settings.about_video_url) : '';

  return (
    <div className="pt-24 pb-16">
      {/* Header */}
      <section className="relative">
        <div className="absolute inset-0 hero-radial pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 relative">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#00b4d8] font-semibold mb-3">About Us</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-3xl">Decades of diving experience. One family of divers.</h1>
          <p className="mt-5 text-white/80 max-w-2xl text-base sm:text-lg leading-relaxed">
            Arab Diver Tours is a proud division of <span className="text-[#c8e600] font-semibold">Aladdin Lamp Tourism</span> — established in Dubai in 1998.
          </p>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <div className="order-2 lg:order-1">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#c8e600] font-semibold mb-3">Our Story</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Born by the sea. Built for divers.</h2>
            <div className="mt-5 space-y-4 text-white/75 leading-relaxed text-sm sm:text-base">
              <p>Arab Diver Tours is a proud division of the parent company, <strong>Aladdin Lamp Tourism</strong>, established in Dubai in 1998. With decades of experience in the tourism industry, our founder, <strong>Bashar Al-Faquih</strong>, was born in the coastal emirate of <strong>Fujairah, UAE</strong>, and channeled his lifelong passion for the sea into creating an iconic dive centre in Musandam, Khasab.</p>
              <p>This hidden gem is a marine wonder — the rich waters of the Indian Ocean bring vibrant marine life each winter and spring, making it a haven for divers. As someone born in Fujairah, a coastal city in the UAE, Bashar has been diving since 2009.</p>
              <p>But our passion is not just diving — we offer a full spectrum of courses with highly qualified instructors in <strong>PADI, TDI / SDI</strong> and <strong>CMAS</strong>. For us, it’s not just about being a customer; it’s about being part of the Arab Diver family.</p>
              <p>Join us now and become a member of this big family — where our priority is always the community, and we are with you every step of the way.</p>
            </div>
            <blockquote className="mt-6 border-l-2 border-[#c8e600] pl-4 text-white italic font-display text-lg">
              “Embark on an unforgettable journey with us — where each ripple narrates a tale and each wave extends an invitation to discover the mysteries of the ocean.”
            </blockquote>
          </div>
          <div className="order-1 lg:order-2 relative">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-white/10">
              <img src="/about/cave-diver.png" alt="Diver exploring an underwater cave" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-[#0a1628]/35 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-[#0a1628]/70 backdrop-blur px-3 py-1.5 text-xs">
                  <Sparkles className="h-3.5 w-3.5 text-[#c8e600]" /> Trusted by divers across the Gulf
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-10" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {[
              { k: 'Years Experience',     v: settings?.stat_years   || '25+'   },
              { k: 'Divers Trained',       v: settings?.stat_divers  || '2000+' },
              { k: 'Courses Offered',      v: settings?.stat_courses || '500+'  },
              { k: 'TripAdvisor Rating',   v: settings?.stat_rating  || '5★'   },
            ].map((s) => (
              <Card key={s.k} className="rounded-2xl border-white/10 bg-white/5 p-6 backdrop-blur">
                <div className="font-display text-3xl sm:text-4xl font-semibold text-[#c8e600]">{s.v}</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/55">{s.k}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Why Choose Us" title="International standards. Genuine care." />
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              { i: ShieldCheck, t: 'Multi-agency expertise', d: 'PADI, TDI / SDI and CMAS — the world’s most respected diver-training agencies, all under one roof.' },
              { i: Heart,        t: 'A genuine family', d: 'We don’t do mass tourism. Every diver is welcomed personally, and many of our customers return year after year.' },
              { i: Anchor,       t: 'Khasab, our home',  d: 'Our dive centre is in Musandam, Khasab — a marine wonder where the Indian Ocean meets the Arabian Gulf.' },
            ].map(({ i: Icon, t, d }) => (
              <Card key={t} className="rounded-2xl border-white/10 bg-white/5 p-6">
                <div className="h-11 w-11 rounded-xl bg-[#00b4d8]/15 text-[#00b4d8] inline-flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                <div className="mt-4 font-display text-lg font-semibold">{t}</div>
                <p className="mt-2 text-sm text-white/65 leading-relaxed">{d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Embedded video */}
      {aboutEmbed && (
        <section className="py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl overflow-hidden border border-white/10 bg-black aspect-video">
              <iframe data-testid="about-embedded-video" src={aboutEmbed} title="About Arab Diver Tours" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" frameBorder="0" />
            </div>
          </div>
        </section>
      )}

      {/* How to start */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="How to Start" title="Three steps to get diving." />
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              { i: GraduationCap, t: 'Make an Appointment', d: 'Tell us your goals, experience level and availability. We tailor every program to the diver in front of us.' },
              { i: ShieldCheck,   t: 'Choose Your Course',   d: 'From Snorkeler to Trimix — SDI for recreational, TDI for technical, PADI for OW — we’ll map the right pathway for you.' },
              { i: Heart,         t: 'Start Diving',         d: 'Theory online, skills in the pool, dives in the sea. Short, structured and totally focused on your safety.' },
            ].map(({ i: Icon, t, d }, idx) => (
              <Card key={t} className="rounded-2xl border-white/10 bg-white/5 p-6 relative overflow-hidden">
                <div className="absolute top-4 right-4 text-[11px] uppercase tracking-[0.22em] text-white/35 font-mono-tight">0{idx + 1}</div>
                <div className="h-11 w-11 rounded-xl bg-[#00b4d8]/15 text-[#00b4d8] inline-flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                <div className="mt-4 font-display text-lg font-semibold">{t}</div>
                <p className="mt-2 text-sm text-white/65 leading-relaxed">{d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
